import { useLocation, useParams } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import ProgressBar from "@/components/ProgressBar";
import QuestionContentDisplay from "@/pages/quiz/base/components/progressHeader/components/QuestionContentDisplay";
import { ProgressBarTheme } from "@/types";
import { useQuizContext } from "@/pages/quiz/context";
import { QuizIDs } from "@/pages/quiz/context/common";

const GOALS_LABELS = {
  1: "GOALS",
  2: "BODY",
  3: "HEALTH",
  4: "LAST STEP",
};

enum NEW_INTAKE_GOALS_LABELS {
  "GOALS" = "GOALS",
  "MEDICAL_HISTORY" = "Medical History",
}

const FORM_CHUNK_SIZE = 10;

interface IProps {
  isHideBackButton?: boolean;
  hideProgressBar?: boolean;
  customOnBack?: () => void;
}

const ProgressHeader = ({
  isHideBackButton = false,
  hideProgressBar = false,
  customOnBack,
}: IProps) => {
  const { questions, selectedQuestionIdx, selectedQuestion, onBack } =
    useQuizContext();
  const { pathname } = useLocation();
  const { quizId } = useParams();

  if (hideProgressBar) {
    return null;
  }

  const isQuizPage = pathname.startsWith("/quiz");

  const questionsAmount = questions?.length ?? 0;

  const currentPosition = selectedQuestionIdx + 1;

  const currentFormChunkIndex = Math.ceil(
    (currentPosition ?? 0) / FORM_CHUNK_SIZE
  );

  const maxChunks = Math.ceil(questionsAmount / FORM_CHUNK_SIZE);

  let chunkMax =
    (currentFormChunkIndex === maxChunks
      ? questionsAmount % FORM_CHUNK_SIZE
      : FORM_CHUNK_SIZE) || 10;

  // is displayed in progress bar
  let currentProgressPosition = Math.max(
    ((currentPosition - 1) % FORM_CHUNK_SIZE) + 1,
    1
  );

  if (!isQuizPage) {
    chunkMax = questions?.length ?? 1;
    currentProgressPosition = selectedQuestionIdx + 1;
  }

  const showBackButton = currentPosition > 1 && !isHideBackButton;
  const withProgressBar = true;

  const progressBarTheme =
    quizId === QuizIDs["13000"] ||
    quizId === QuizIDs["13500"] ||
    quizId === QuizIDs["11000"] ||
    quizId === QuizIDs["12000"]
      ? ProgressBarTheme.DARK
      : ProgressBarTheme.LIGHT;

  const headerLabel = (() => {
    if (
      quizId === QuizIDs["13000"] ||
      quizId === QuizIDs["13500"] ||
      quizId === QuizIDs["11000"] ||
      quizId === QuizIDs["12000"]
    ) {
      // DESC: 15 - count of questions with GOALS title in figma
      if (currentPosition <= 15) {
        return NEW_INTAKE_GOALS_LABELS.GOALS;
      }
      return NEW_INTAKE_GOALS_LABELS.MEDICAL_HISTORY;
    }

    return GOALS_LABELS[currentFormChunkIndex as keyof typeof GOALS_LABELS];
  })();

  return (
    <div>
      <div className="mb-2.5 lg:mb-3.5 flex justify-center relative">
        {showBackButton && (
          <div
            onClick={customOnBack ? customOnBack : onBack}
            className="text-xs font-sans sm:text-sm absolute left-0 font-bold text-gray-darker flex gap-1 items-center cursor-pointer hover:bg-silver-2 transition-all py-1 px-2.5 -ml-2.5 rounded-full"
          >
            <FaArrowLeftLong />
            <span>Back</span>
          </div>
        )}
        <span className="text-xs sm:text-sm font-bold text-gray-dark tracking-[.1em]">
          {headerLabel}
        </span>
        {!!questionsAmount && (
          <span className="text-xs absolute right-0 font-bold text-gray-darker">
            {currentProgressPosition}/{chunkMax}
          </span>
        )}
      </div>
      {withProgressBar && !!questionsAmount && (
        <div className="mt-4 sm:mt-5 lg:mt-6 mb-5 sm:mb-7 lg:mb-8">
          <ProgressBar
            progress={currentProgressPosition}
            max={chunkMax}
            theme={progressBarTheme}
          />
        </div>
      )}

      {selectedQuestion && !!selectedQuestion.attributes.question && (
        <QuestionContentDisplay
          question={selectedQuestion.attributes.question}
          prompt={selectedQuestion.attributes.prompt}
          customHTML={selectedQuestion.attributes.customHTML}
          descriptionBlock={selectedQuestion.attributes.descriptionBlock}
        />
      )}
    </div>
  );
};

export { ProgressHeader };
